import { FC, useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { SwiperSlide } from "swiper/react";
import PosSwiper from "../pos-swiper/PosSwiper";
import { cloneDeep } from "lodash";

import { CustomButton } from "../../../atoms";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import { updateCartSessionItem } from "../../../../redux/actions/cartAction";
import { getAllReleasesAction } from "../../../../redux/actions/customerReleaseSignatureAction";
import { IItemRelease } from "../../../../typings/interfaces/pos/cart-item";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";

const RELEASES_PER_PAGE = 36;

/**
 * Customization > Releases: tag the item with releases set up in
 * Admin > Releases (e.g. "Cleaning Release"). They print at the bottom of the
 * ticket and on a separate slip for the customer to sign.
 */
const ReleaseSection: FC = () => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const { selectedCartItem } = useAppSelector((state) => state.cart);
  const { releases, getAllLoading, getAllError } = useAppSelector(
    (state) => state.release
  );

  useEffect(() => {
    dispatch(getAllReleasesAction());
  }, []);

  useEffect(() => {
    if (getAllError) snackbar.showSnackbar(getAllError, "error");
  }, [getAllError]);

  const activeReleases = [...(releases ?? [])]
    .filter((release) => release.active !== false && release.id != null)
    .sort((a, b) => a.name.localeCompare(b.name));

  const isAdded = (id: number) =>
    Boolean(selectedCartItem?.releases?.some((release) => release.id === id));

  const onSelectRelease = (release: IItemRelease) => {
    const cartItem = cloneDeep(selectedCartItem);
    if (!cartItem) return;
    const current = cartItem.releases ?? [];
    cartItem.releases = isAdded(release.id)
      ? current.filter((item) => item.id !== release.id)
      : [...current, release];
    dispatch(updateCartSessionItem({ cartItem }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "1.71428571rem",
        background: Colors.BACKGROUND_GRAY,
        borderRadius: "0.71rem",
        padding: "0.928571429rem",
      }}
    >
      <Typography sx={{ fontSize: "var(--fs-lg)", color: Colors.TEXT_GRAY_DARK }}>
        Releases
      </Typography>

      {getAllLoading ? (
        <CircularProgress sx={{ mx: "auto" }} />
      ) : activeReleases.length === 0 ? (
        <Typography sx={{ fontSize: "var(--fs-base)", color: Colors.TEXT_GRAY_DARK }}>
          No releases set up. Add them in Admin &gt; Releases.
        </Typography>
      ) : (
        <PosSwiper style={{ width: "72rem" }} spaceBetween={2} direction="horizontal">
          {[...Array(Math.ceil(activeReleases.length / RELEASES_PER_PAGE))].map(
            (_, pageIndex) => (
              <SwiperSlide
                key={pageIndex}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignContent: "flex-start",
                  width: "72rem",
                  gap: "1.07142857rem",
                  rowGap: "0.857142857rem",
                  height: "46.7142857rem",
                }}
              >
                {activeReleases
                  .slice(pageIndex * RELEASES_PER_PAGE, (pageIndex + 1) * RELEASES_PER_PAGE)
                  .map((release) => {
                    const selected = isAdded(release.id as number);
                    return (
                      <CustomButton
                        key={release.id}
                        name="release-button"
                        aria-pressed={selected}
                        sx={{
                          width: "17.0064286rem",
                          minWidth: "17.0064286rem",
                          height: "4.28571429rem",
                          padding: "1.07142857rem 0.857142857rem",
                          background: selected ? Colors.SECONDARY : Colors.MAIN_GRAY,
                          borderRadius: "0.71rem",
                          textTransform: "capitalize",
                          color: Colors.WHITE,
                          fontSize: "var(--fs-base)",
                          lineHeight: 1.2,
                          "&:hover": {
                            background: `${selected ? Colors.SECONDARY : Colors.MAIN_GRAY} !important`,
                          },
                        }}
                        onClick={() =>
                          onSelectRelease({
                            id: release.id as number,
                            name: release.name,
                            agreementText: release.agreementText ?? null,
                          })
                        }
                      >
                        {release.name}
                      </CustomButton>
                    );
                  })}
              </SwiperSlide>
            )
          )}
        </PosSwiper>
      )}
    </Box>
  );
};

export default ReleaseSection;
